import { motion } from "framer-motion";
import { useRef } from "react";
import { MapPinned, Fuel, Coffee, Map, Radar, TriangleAlert } from "lucide-react";

const features = [
    {
        icon: MapPinned,
        title: "Rota & maliyet",
        desc: "A'dan B'ye en iyi yol — kilometre, süre ve toplam tahmini maliyet tek ekranda.",
        accent: "cyan",
    },
    {
        icon: Fuel,
        title: "HGS & yakıt tahmini",
        desc: "Otoyol geçiş ücretleri (HGS) ve araç tipine göre gerçekçi yakıt hesaplaması.",
        accent: "magenta",
    },
    {
        icon: Radar,
        title: "Radar uyarıları",
        desc: "Rotan üzerindeki sabit, ortalama hız ve mobil radarlar haritada — 300 m önceden sesli uyarı.",
        accent: "magenta",
    },
    {
        icon: TriangleAlert,
        title: "KGM yol durumu",
        desc: "Hangi yol açık, hangisi kapalı? KGM canlı verisiyle güzergâhın o anki durumu.",
        accent: "cyan",
    },
    {
        icon: Coffee,
        title: "Mola yerleri",
        desc: "Güzergâh üzerinde restoran, mescit, tuvalet, oto yıkama — filtreleyip seçin.",
        accent: "cyan",
    },
    {
        icon: Map,
        title: "Harita odaklı arayüz",
        desc: "Tek dokunuşla planla. Sessiz ve hızlı harita, gereksiz menü yok.",
        accent: "magenta",
    },
];

const container = {
    hidden: {},
    show: {
        transition: {
            staggerChildren: 0.08,
            delayChildren: 0.1,
        },
    },
};

const item = {
    hidden: { opacity: 0, y: 28 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
};

function TiltCard({ children, className = "", ...rest }) {
    const ref = useRef(null);

    const handleMove = (e) => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        el.style.transform = `perspective(900px) rotateX(${py * -6}deg) rotateY(${px * 8}deg) translateY(-4px)`;
        el.style.setProperty("--mx", `${(px + 0.5) * 100}%`);
        el.style.setProperty("--my", `${(py + 0.5) * 100}%`);
    };
    const handleLeave = () => {
        const el = ref.current;
        if (!el) return;
        el.style.transform = "perspective(900px) rotateX(0) rotateY(0) translateY(0)";
    };

    return (
        <div
            ref={ref}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            className={`tilt-card ${className}`}
            {...rest}
        >
            {children}
        </div>
    );
}

export default function Features() {
    return (
        <section
            id="ozellikler"
            data-testid="features-section"
            className="relative py-28 sm:py-36 px-5 sm:px-8 max-w-7xl mx-auto"
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-2xl"
            >
                <div className="text-[#00F0FF] text-xs uppercase tracking-[0.22em] font-medium mb-5">
                    Özellikler
                </div>
                <h2 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                    Yolculuğu önceden <span className="grad-text">planla</span>, sürprizleri boşver.
                </h2>
                <p className="mt-5 text-white/60 text-base sm:text-lg max-w-xl leading-relaxed">
                    Tek bir arayüzde rota, maliyet ve molalar. Bagajı hazırla, gerisi bizde.
                </p>
            </motion.div>

            <motion.div
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
                {features.map((f, i) => {
                    const Icon = f.icon;
                    const isCyan = f.accent === "cyan";
                    return (
                        <motion.div
                            key={i}
                            variants={item}
                            data-testid={`feature-card-${i}`}
                        >
                            <TiltCard className="feature-card relative p-6 rounded-2xl bg-[#111115] border border-white/[0.06] overflow-hidden h-full">
                                {/* Cursor spotlight on card */}
                                <div
                                    className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                                    style={{
                                        background: `radial-gradient(circle at var(--mx, 50%) var(--my, 50%), ${isCyan ? "rgba(0,240,255,0.12)" : "rgba(255,45,146,0.12)"} 0%, transparent 60%)`,
                                    }}
                                />
                                <div
                                    className={`absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-40 ${
                                        isCyan ? "bg-[#00F0FF]/30" : "bg-[#FF2D92]/30"
                                    }`}
                                    aria-hidden="true"
                                />
                                <div className="tilt-inner relative">
                                    <div
                                        className={`w-11 h-11 rounded-xl flex items-center justify-center border ${
                                            isCyan
                                                ? "bg-[#00F0FF]/10 border-[#00F0FF]/25 text-[#00F0FF]"
                                                : "bg-[#FF2D92]/10 border-[#FF2D92]/25 text-[#FF2D92]"
                                        }`}
                                    >
                                        <Icon size={20} strokeWidth={2} />
                                    </div>
                                    <h3 className="mt-5 font-display font-semibold text-xl tracking-tight">
                                        {f.title}
                                    </h3>
                                    <p className="mt-2.5 text-sm text-white/55 leading-relaxed">
                                        {f.desc}
                                    </p>
                                </div>
                            </TiltCard>
                        </motion.div>
                    );
                })}
            </motion.div>
        </section>
    );
}
